import { Link } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Box, Button, Code, Collapse, Divider, Heading, List, ListItem, Text, useDisclosure } from "@chakra-ui/react";
import { PipelineImport } from "../../../api/types";
import { typedSxMap } from "../../../utils/typedSxMap";
import { formatFileSize } from "./utils/formatFileSize";
import OverviewTimeline from "./OverviewTimeline";

const styles = typedSxMap({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xl)",
  },
  firstBox: {
    marginTop: "-20px", // sorry :)
  },
  sectionHeading: {
    fontSize: "var(--font-size-xl)",
    color: "var(--color-text-strong)",
  },
  divider: {
    borderColor: "var(--color-border)",
    opacity: 1,
    borderBottomWidth: "1px",
  },
  summary: {
    display: "flex",
    margin: "var(--m-xxs) 0 ",
    gap: "var(--m)",
  },
  summaryItem: {
    boxShadow: "inset -1px 0 var(--color-text-soft)",
    paddingRight: "var(--m-xxl)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxs)",
    "&:last-of-type": {
      boxShadow: "none",
    },
    "& > p": {
      marginBottom: 0,
      "&:first-of-type": {
        color: "var(--color-text-soft)",
      },
    },
  },
  summaryItemValue: {
    fontSize: "var(--font-size-xl)",
    fontWeight: "var(--external-fontWeights-normal)",
    color: "var(--color-text-strong)",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxxs)",
  },
  detailsItem: {
    display: "flex",
    justifyContent: "",
    color: "var(--color-text-strong)",

    "& > span": {
      textAlign: "left",
      "&:first-of-type": {
        maxWidth: "350px",
        color: "var(--color-text-soft)",
        width: "50%",
      },
    },
  },
  metadataToggleButton: {
    padding: 0,
    margin: 0,
    height: "auto",
    minWidth: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    color: "var(--color-primary)",
    fontWeight: "normal",
    fontSize: "var(--font-size-s)",
    _hover: {
      textDecoration: "underline",
    },
  },
  metadataCode: {
    mt: "var(--m-xxs)",
    borderRadius: "var(--radius-m)",
    overflow: "auto",
    maxHeight: "300px",
    whiteSpace: "pre",
    bg: "var(--color-code-bg)",
    color: "var(--color-text-strong)",
    padding: "var(--m-s)",
    fontSize: "var(--font-size-s)",
    fontFamily: "monospace",
    width: "100%",
    border: "1px solid var(--color-border-soft)",
  },
});

type OverviewSectionProps = {
  pipelineImport: PipelineImport;
};

export default function OverviewSection({ pipelineImport }: OverviewSectionProps) {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Box sx={styles.container}>
      <Box sx={styles.firstBox}></Box>
      {(pipelineImport.status === "complete" || pipelineImport.status === "importing") && (
        <Box>
          <Heading as="h3" sx={styles.sectionHeading}>
            Rows Imported
          </Heading>
          <Divider sx={styles.divider} />
          <Box sx={styles.summary}>
            <Box sx={styles.summaryItem}>
              <Text>Accepted</Text>
              <Text sx={styles.summaryItemValue}>{pipelineImport.dst_row_status.accepted}</Text>
            </Box>
            <Box sx={styles.summaryItem}>
              <Text>Rejected</Text>
              <Text sx={styles.summaryItemValue}>{pipelineImport.dst_row_status.failed}</Text>
            </Box>
            <Box sx={styles.summaryItem}>
              <Text>Pending</Text>
              <Text sx={styles.summaryItemValue}>{pipelineImport.dst_row_status.created}</Text>
            </Box>
          </Box>
        </Box>
      )}

      <Box>
        <Heading as="h3" sx={styles.sectionHeading}>
          Details
        </Heading>
        <Divider sx={styles.divider} />
        <List sx={styles.details}>
          <ListItem sx={styles.detailsItem}>
            <span>Schema</span>
            <Link to={`/schema/${pipelineImport.schema?.id}`}>
              <span>{pipelineImport.schema?.name}</span>
            </Link>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>File name</span>
            <span>{pipelineImport.src_file_name}</span>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>File size</span>
            <span>{formatFileSize(pipelineImport.src_file_size)}</span>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>Number of rows</span>
            <span>{pipelineImport.src_num_rows}</span>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>Number of columns</span>
            <span>{pipelineImport.src_num_columns}</span>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>Metadata</span>
            <span>
              <Button
                onClick={onToggle}
                variant="link"
                sx={styles.metadataToggleButton}
                size="sm"
                rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}>
                {isOpen ? "Hide" : "Show"}
              </Button>
            </span>
          </ListItem>
          <Collapse in={isOpen} animateOpacity style={{ width: "100%" }}>
            <Code
              sx={styles.metadataCode}
              overflowX="auto"
              display="block"
              whiteSpace="pre"
              children={JSON.stringify(pipelineImport.metadata || {}, null, 2)}
            />
          </Collapse>
        </List>
      </Box>

      <Box>
        <Heading as="h3" sx={styles.sectionHeading}>
          Timeline
        </Heading>
        <Divider sx={styles.divider} />
        <OverviewTimeline timelineData={pipelineImport.status_history} />
      </Box>
    </Box>
  );
}
